import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Label1, Skeleton, ToggleButtonGroup, Body1, Link } from '@sainsburys-tech/fable'

import FulfilmentOptionButton from '../FulfilmentOptionButton/FulfilmentOptionButton'
import FulfilmentContent from '../FulfilmentContent/FulfilmentContent'
import { FULFILMENT_TYPES, getAvailabilityMessage, getFulfilmentOption } from '../fulfilmentBoxUtils'
import FulfilmentPassDrawer from '../../FulfilmentPassDrawer/FulfilmentPassDrawer'
import FulfilmentInfoBox from '../FulfilmentInfoBox/FulfilmentInfoBox'
import FulfilmentPassAlert from '../FulfilmentPassAlert/FulfilmentPassAlert'
import FulfilmentPassEligibility from '../FulfilmentPassEligibility/FulfilmentPassEligibility'
import { useAddFulfilmentPass, BASKET_API_STATUS } from '../../../javascript/hooks/useAddFulfilmentPass'
import { useFulfilmentPassStatus } from '../../../javascript/hooks/useFulfilmentPassStatus'
import { pushEventAndTrack } from '@/javascript/helpers/taggingSerializer'

export const FulfilmentBoxV2 = ({
  collectable,
  collectionMessage,
  collectionMessageKey,
  collectionIcon,
  collectionStore,
  deliverable,
  deliveryMessage,
  deliveryMessageKey,
  deliveryIcon,
  deliveryPostcode,
  openStoreSelector,
  onShowDeliveryForm,
  deliveryPostcodeForm = null,
  isLoading,
  collectionAdditionalContent = null,
  deliveryAdditionalContent = null,
  supplierInfoCTA = null,
  showTopBorder = false,
  userId,
  hasUserIdLoaded,
  productPrice,
  fulfilmentPassConfig = {},
  isEligibleFulfilmentPassProduct
}) => {
  const { SHOW_PURCHASE_PASS, SHOW_PRODUCT_ELIGIBILITY: showFulfilmentPassProductEligibility } = fulfilmentPassConfig

  const showPurchaseFulfilmentPass = SHOW_PURCHASE_PASS && isEligibleFulfilmentPassProduct

  const isFulfilmentPassEnabled = showPurchaseFulfilmentPass || showFulfilmentPassProductEligibility

  // Add state for the fulfilment pass drawer
  const [isPassDrawerOpen, setIsPassDrawerOpen] = useState(false)

  const { basketApiStatus, addPassToTrolley, isAddingPassToTrolley } = useAddFulfilmentPass()
  const fulfilmentPassDetails = useFulfilmentPassStatus({
    userId,
    hasUserIdLoaded,
    enabled: isFulfilmentPassEnabled
  })

  // If showing delivery input form, don't show previous availability in delivery option button
  const deliveryOptionKey = deliveryPostcodeForm ? null : deliveryMessageKey
  const deliveryOptionIcon = deliveryPostcodeForm ? null : deliveryIcon

  const fulfilmentOptions = [
    {
      type: FULFILMENT_TYPES.COLLECTION,
      ...getFulfilmentOption(collectionMessageKey, collectionIcon, !collectable),
      isDisabled: !collectable
    },
    {
      type: FULFILMENT_TYPES.DELIVERY,
      ...getFulfilmentOption(deliveryOptionKey, deliveryOptionIcon, !deliverable),
      isDisabled: !deliverable
    }
  ]

  const combinedDeliveryAdditionalContent = (
    <>
      {deliveryAdditionalContent}
      {showFulfilmentPassProductEligibility && (
        <FulfilmentPassEligibility
          productPrice={productPrice}
          isEligibleProduct={isEligibleFulfilmentPassProduct}
          passThreshold={fulfilmentPassDetails?.passThreshold}
          hasActivePass={fulfilmentPassDetails?.hasActivePass}
        />
      )}
    </>
  )

  const fulfilmentContent = [
    {
      type: FULFILMENT_TYPES.COLLECTION,
      isValidFulfilment: collectable,
      message: getAvailabilityMessage(collectionMessage, collectionStore),
      location: collectionStore,
      ctaCopy: 'Change store',
      onCtaClick: openStoreSelector,
      additionalContent: collectionAdditionalContent
    },
    {
      type: FULFILMENT_TYPES.DELIVERY,
      isValidFulfilment: deliverable,
      message: getAvailabilityMessage(deliveryMessage, deliveryPostcode, deliveryMessageKey),
      location: deliveryPostcode,
      ctaCopy: 'Change postcode',
      onCtaClick: onShowDeliveryForm,
      additionalContent: combinedDeliveryAdditionalContent,
      supplierInfoCTA,
      deliveryPostcodeForm
    }
  ]

  const [selectedType, setSelectedType] = useState(fulfilmentOptions.find(option => !option.isDisabled)?.type)

  useEffect(() => {
    const selectedOption = fulfilmentOptions.find(option => option.type === selectedType)

    // If selectedType doesn't exist or is now disabled, select the first available option
    if (!selectedOption || selectedOption.isDisabled) {
      const firstAvailableOption = fulfilmentOptions.find(option => !option.isDisabled)
      setSelectedType(firstAvailableOption?.type)
    }
  }, [collectable, deliverable])

  if (!collectable && !deliverable) {
    return null
  }

  const selectedFulfilmentContent = fulfilmentContent.find(option => option.type === selectedType)

  const showLoadingSkeleton =
    isFulfilmentPassEnabled && (fulfilmentPassDetails?.status === 'loading' || fulfilmentPassDetails?.status === 'idle')

  const trackLearnMoreClick = () => {
    pushEventAndTrack({
      eventInfo: {
        eventAction: 'fulfilment pass learn more',
        eventName: 'fulfilmentPass_learnMore'
      }
    })
  }

  // Fulfilment pass CTA
  const messageToOpenPassDrawer = (
    <div>
      <Label1 className='ds-leading-none ds-font-semibold ds-mb-2'>A whole year of home delivery</Label1>
      <Body1>Includes all available slots from morning to evening. £40 a year.</Body1>
      <Link
        as='button'
        onClick={() => {
          trackLearnMoreClick()
          setIsPassDrawerOpen(true)
        }}
        className='ds-font-semibold ds-underline ds-mt-2 ds-inline-block ds-text-left'
      >
        Learn more about Argos Plus
      </Link>
    </div>
  )

  return (
    <div className={`fulfilment-box-v2${showTopBorder ? ' top-border' : ''}`}>
      {showLoadingSkeleton && <Skeleton data-testid='fulfilment-loading-skeleton' width='100%' height='340px' />}
      {!showLoadingSkeleton && (
        <>
          <Label1 className='ds-pb-3 ds-text-monochrome-darker'>How to get your item</Label1>
          {showPurchaseFulfilmentPass &&
            fulfilmentPassDetails?.status === 'success' &&
            !fulfilmentPassDetails?.hasActivePass && (
              <>
                {/* If customer has tried adding fulfilment pass to their trolley, show alert */}
                {basketApiStatus !== BASKET_API_STATUS.IDLE ? (
                  <FulfilmentPassAlert passStatus={basketApiStatus} />
                ) : (
                  <FulfilmentInfoBox>{messageToOpenPassDrawer}</FulfilmentInfoBox>
                )}
                <FulfilmentPassDrawer
                  isLoggedIn={!!userId}
                  isOpen={isPassDrawerOpen}
                  onClose={() => setIsPassDrawerOpen(false)}
                  addPassToTrolley={addPassToTrolley}
                  isAddingPassToTrolley={isAddingPassToTrolley}
                />
              </>
            )}
          <ToggleButtonGroup
            label='Toggle between collection and delivery options'
            value={selectedType}
            className='button-group ds-mb-3'
            onChange={setSelectedType}
          >
            {fulfilmentOptions.map((option, index) => (
              <FulfilmentOptionButton
                {...option}
                key={`${option.type}-${index}`}
                value={option.type}
                isActive={selectedType === option.type}
                isLoading={isLoading}
              />
            ))}
          </ToggleButtonGroup>
          <FulfilmentContent {...selectedFulfilmentContent} isLoading={isLoading} />
        </>
      )}
    </div>
  )
}

FulfilmentBoxV2.propTypes = {
  collectable: PropTypes.bool,
  collectionMessage: PropTypes.string,
  collectionMessageKey: PropTypes.string,
  collectionIcon: PropTypes.string,
  collectionStore: PropTypes.string,
  deliverable: PropTypes.bool,
  deliveryMessage: PropTypes.string,
  deliveryMessageKey: PropTypes.string,
  deliveryIcon: PropTypes.string,
  deliveryPostcode: PropTypes.string,
  openStoreSelector: PropTypes.func,
  onShowDeliveryForm: PropTypes.func,
  deliveryPostcodeForm: PropTypes.node,
  isLoading: PropTypes.bool,
  collectionAdditionalContent: PropTypes.node,
  deliveryAdditionalContent: PropTypes.node,
  showTopBorder: PropTypes.bool,
  supplierInfoCTA: PropTypes.node,
  userId: PropTypes.string,
  hasUserIdLoaded: PropTypes.bool,
  productPrice: PropTypes.number,
  fulfilmentPassConfig: PropTypes.shape({
    SHOW_PRODUCT_ELIGIBILITY: PropTypes.bool,
    SHOW_PURCHASE_PASS: PropTypes.bool
  }),
  isEligibleFulfilmentPassProduct: PropTypes.bool
}
